import { z } from 'zod';
import { PointSchema } from './geojson';

export const CameraStreamTypeSchema = z.enum([
  'snapshot',
  'hls',
  'mjpeg',
  'rtsp',
  'mp4_clip',
  // A YouTube live stream rendered via the YouTube IFrame API (Phase Q.1).
  // The row carries no fetchable snapshot/manifest of its own; stream_url is a
  // youtube.com/embed/{videoId} URL (direct + ex-CamStreamer) or a channel-live
  // URL the player resolves to a current videoId at view time.
  'youtube_live',
]);
export type CameraStreamType = z.infer<typeof CameraStreamTypeSchema>;

/**
 * Operator-facing labels for `stream_type` (W6 raw-enum legibility, ledger §3.D) so the
 * camera overlay never leaks `mp4_clip`. `mp4_clip` reads "Looping clip" and `snapshot`
 * "Snapshot" — NEVER "Live" (the #256 not-live-video founder ruling: an on-demand
 * looping MP4 / periodic still is not live video). hls/mjpeg/rtsp are live manifests.
 */
export const CAMERA_STREAM_TYPE_LABELS: Record<CameraStreamType, string> = {
  snapshot: 'Snapshot',
  hls: 'Live stream',
  mjpeg: 'Live stream',
  rtsp: 'Live stream',
  mp4_clip: 'Looping clip',
  youtube_live: 'YouTube live',
};

/** Humanized `stream_type` label; an unrecognised value falls back to "Clip" — never
 *  the raw token, and never "Live" (#256). */
export function cameraStreamTypeLabel(streamType: string): string {
  return CAMERA_STREAM_TYPE_LABELS[streamType as CameraStreamType] ?? 'Clip';
}

/**
 * Known camera `source` providers whose acronym/casing the generic prettifier would get
 * wrong. `source` is free-text (`z.string()`), so anything not listed falls through to
 * {@link cameraSourceLabel}'s generic humanizer.
 */
export const CAMERA_SOURCE_LABELS: Record<string, string> = {
  ontario511: 'Ontario 511',
  ab511: 'Alberta 511',
  qc511: 'Québec 511',
  alertwest: 'AlertWest',
  ipcamlive: 'IPCamLive',
  earthcam: 'EarthCam',
  wetmet: 'WetMet',
  nswebcams: 'Nova Scotia Webcams',
  eccc: 'ECCC',
  ecccswob: 'ECCC SWOB',
  ccg: 'CCG',
  mto: 'MTO',
  mtq: 'MTQ',
};

/**
 * Humanized camera `source` (W6 raw-enum legibility) so the overlay reads "Ontario 511"
 * not `ontario511`. Known providers map through {@link CAMERA_SOURCE_LABELS}; everything
 * else is prettified generically — split on separators + digit boundaries, title-cased —
 * so an unlisted `source` still reads as words, never the raw lowercase token.
 */
export function cameraSourceLabel(source: string): string {
  const known = CAMERA_SOURCE_LABELS[source];
  if (known) return known;
  const pretty = source
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])(\d)/gi, '$1 $2')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return pretty.length > 0 ? pretty : source;
}

export const CameraHeadingSourceSchema = z.enum(['unknown', 'upstream', 'ai_inference', 'manual']);
export type CameraHeadingSource = z.infer<typeof CameraHeadingSourceSchema>;

export const CameraFovSourceSchema = z.enum(['unknown', 'upstream', 'ai_inference', 'manual']);
export type CameraFovSource = z.infer<typeof CameraFovSourceSchema>;

export const CameraStatusSchema = z.enum([
  'live',
  'snapshot',
  'offline',
  'error',
  'degraded',
  'unknown',
]);
export type CameraStatus = z.infer<typeof CameraStatusSchema>;

export const CameraPermissionStatusSchema = z.enum([
  'public_open',
  'attribution_required',
  'partnership_required',
  // Founder authorized OPERATOR DISPLAY of this feed (e.g. the 2026-06-24 air-cams,
  // migration 0108 / tracker #430). Distinct from public_open — the license basis
  // remains a partnership; this records the display grant, not an open license.
  'partner_authorized',
  'unknown',
]);
export type CameraPermissionStatus = z.infer<typeof CameraPermissionStatusSchema>;

export const CameraIntegrationStatusSchema = z.enum([
  'in_platform',
  'sourced_not_ingested',
  'cataloged_for_later',
  'excluded',
]);
export type CameraIntegrationStatus = z.infer<typeof CameraIntegrationStatusSchema>;

export const CameraFeaturePropertiesSchema = z.object({
  name: z.string(),
  source: z.string(),
  external_id: z.string(),
  heading_deg: z.number(),
  fov_deg: z.number(),
  range_m: z.number(),
  /** false when heading_deg came from a heuristic default. The UI renders
   *  an omnidirectional disc in that case rather than a misleading cone. */
  heading_known: z.boolean(),
  /** Provenance of the current heading_deg value. */
  heading_source: CameraHeadingSourceSchema,
  /** Present only when heading_source='ai_inference'; 0-1 confidence
   *  reported by the vision model. */
  heading_confidence: z.number().nullable(),
  heading_inferred_at: z.string().datetime({ offset: true }).nullable(),
  /** Independent provenance for fov_deg / range_m. */
  fov_known: z.boolean(),
  fov_source: CameraFovSourceSchema,
  fov_confidence: z.number().nullable(),
  stream_type: CameraStreamTypeSchema,
  /** Playback media format, lifted from metadata.media_format. Decouples the
   *  player's strategy from the stream_type CLASSIFICATION: a QC 511 / MTQ
   *  camera is classified stream_type='snapshot' (#256 founder ruling — its
   *  on-demand looping-MP4 clip is NOT live video, so no live-coverage tier),
   *  yet media_format='mp4_clip' still routes playback to the looping-MP4
   *  player. null/absent for sources whose stream_type already says it all;
   *  optional so older fixtures/clients omit it. */
  media_format: z.string().nullable().optional(),
  /** Live stream manifest URL (HLS/MJPEG/RTSP). Present on list features so
   *  the side-panel player can mount without a detail round-trip; null for
   *  snapshot-only cameras. */
  stream_url: z.string().nullable(),
  last_snapshot_at: z.string().datetime({ offset: true }).nullable(),
  /** Upstream photo-capture timestamp when the source exposes one (EXIF, feed
   *  metadata, etc.). Distinct from last_snapshot_at, which is when WE last
   *  ingested the frame. NULL when the source gives no capture time — the UI
   *  then falls back to last_snapshot_at and labels it "Ingested" rather than
   *  pretending it is photo time (Stage 5 §5.4). Optional so pre-Stage-5
   *  fixtures and older clients omit it. */
  captured_at: z.string().datetime({ offset: true }).nullable().optional(),
  /** Source-level expected refresh cadence (seconds), lifted from
   *  metadata.refresh_interval_s onto the list feature so the side-panel
   *  freshness timeline renders without a detail round-trip. null when the
   *  cadence isn't known for that source; optional because pre-V.1 fixtures
   *  and older clients omit it. */
  refresh_interval_s: z.number().int().positive().nullable().optional(),
  /** Operational state advanced by the health-check loop. */
  status: CameraStatusSchema,
  /** Analyst-clickable viewer page (distinct from snapshot_url). */
  viewer_url: z.string().nullable(),
  province: z.string().nullable(),
  permission_status: CameraPermissionStatusSchema,
  integration_status: CameraIntegrationStatusSchema,
  is_ptz: z.boolean(),
  smoke_priority: z.boolean(),
  /** True for cameras in the curated smoke-CV run list (E.W.1): geometric
   *  fitness for smoke-detection inference, a strict subset of smoke_priority.
   *  Optional so older fixtures/clients omit it. */
  smoke_cv_eligible: z.boolean().optional(),
  /** Wall curation flag (#273, PART XI Ruling 5.2): part of the wall's curated
   *  ambient pool (vista/lookout/summit lives, a few municipal feeds, AB
   *  fire-tower stills). The wall reel draws AMBIENT seats featured-first.
   *  Operator-owned (desk-set via feature_camera_on_wall). Optional so older
   *  fixtures/clients omit it. */
  wall_featured: z.boolean().optional(),
  /** True for view-time-resolver cams (ALERTWest, Roundshot): rows that carry
   *  only a snapshot_url and re-resolve a CURRENT frame on each fetch, so they
   *  have no stream_url yet are genuinely live when fresh. For these rows the
   *  freshness signal is `status` (live=fresh frame, degraded=resolver stale),
   *  NOT url_health — the indirection probes as OTHER/redirect even when the
   *  frame is fine. Lets the panel show a LIVE state + suppress the false
   *  "Source broken" banner (Q.3.1). Optional so older fixtures/clients omit it. */
  view_time_resolver: z.boolean().optional(),
  /** Snapshot-URL health verdict from the Q.1 rot probe (metadata.url_health),
   *  lifted onto the feature so the map can dim broken markers and the panel
   *  can banner them without a detail round-trip. null when never probed (e.g.
   *  live cams, older fixtures). status='HEALTHY' renders normally; any other
   *  value is treated as unhealthy. checked_at is when the probe last ran. */
  url_health: z
    .object({
      status: z.string(),
      checked_at: z.string().nullable(),
      /** When the source was first observed broken (the "broken since" date in
       *  the panel banner). We have a single probe pass, so this equals
       *  checked_at for HTTP failures; content-placeholder detection stamps it
       *  at first content failure. */
      first_failed_at: z.string().nullable().optional(),
      /** true when the URL is HTTP-healthy but serves a known placeholder image
       *  (e.g. Toronto "Image Unavailable"). */
      content_unavailable: z.boolean().optional(),
    })
    .nullable()
    .optional(),
});
export type CameraFeatureProperties = z.infer<typeof CameraFeaturePropertiesSchema>;

export const CameraFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: CameraFeaturePropertiesSchema,
});
export type CameraFeature = z.infer<typeof CameraFeatureSchema>;

export const CameraFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(CameraFeatureSchema),
});
export type CameraFeatureCollection = z.infer<typeof CameraFeatureCollectionSchema>;

export const CameraDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: CameraFeaturePropertiesSchema.extend({
    snapshot_url: z.string().nullable(),
    stream_url: z.string().nullable(),
    metadata: z.record(z.unknown()).nullable(),
    /** Full audit blob from the heading inference run: model, prompt_version,
     *  reasoning, raw response metadata. Only on the detail endpoint to
     *  keep the list endpoint lean. */
    heading_inference: z.record(z.unknown()).nullable(),
    /** Full audit blob from the FOV inference run (Phase D.1+). */
    fov_inference: z.record(z.unknown()).nullable(),
    fov_inferred_at: z.string().datetime({ offset: true }).nullable(),
    last_health_check_at: z.string().datetime({ offset: true }).nullable(),
    consecutive_failures: z.number().int().nonnegative(),
  }),
});
export type CameraDetail = z.infer<typeof CameraDetailSchema>;
